import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline'
import { LockClosedIcon } from '@heroicons/react/24/solid'
import type { RecipeLedgerRow } from '../RecipeLogStep'

interface SoldPosStepProps {
  recipes: RecipeLedgerRow[]
  onNext: () => void
  onBack: () => void
  isFirst: boolean
}

const SoldPosStep = ({ recipes, onNext, onBack, isFirst }: SoldPosStepProps) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-lg font-semibold text-gray-900">Sold via POS</h2>
      <p className="mt-1 text-sm text-gray-500">
        These quantities are filled in automatically from the orders on the selected date. Nothing to log here.
      </p>
    </div>

    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-100 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipe</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Portions sold</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {recipes.map((recipe) => (
            <tr key={recipe.raw_material_id} className="hover:bg-gray-50">
              <td className="px-4 py-3 whitespace-nowrap">
                <span className="font-medium text-gray-900">{recipe.raw_material_name}</span>
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  {recipe.unit}
                </span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right">
                <span className="inline-flex flex-col items-end gap-1">
                  <span className="inline-flex items-center gap-1.5 text-gray-500">
                    <LockClosedIcon className="h-3 w-3 text-gray-300" />
                    {recipe.sold_portions > 0
                      ? <span className="font-medium text-gray-900">{recipe.sold_portions} portions</span>
                      : <span className="text-gray-300">—</span>
                    }
                  </span>
                  {(recipe.sold_portions_breakdown ?? []).map((b, i) => (
                    <span key={i} className="text-xs text-gray-400">
                      {b.product_name} · {b.variant_name} × {b.portions}
                    </span>
                  ))}
                  {(recipe.sold_portions_by_options ?? []).length > 0 && (
                    <span className="mt-1 flex flex-col items-end gap-0.5 border-t border-gray-100 pt-1">
                      <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">By option combo</span>
                      {recipe.sold_portions_by_options.map((o, i) => (
                        <span key={i} className="text-xs text-gray-400">
                          {o.option_labels || o.option_item_ids.join(', ')} × {o.portions}
                        </span>
                      ))}
                    </span>
                  )}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
      <button
        onClick={onBack}
        disabled={isFirst}
        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-30"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back
      </button>
      <button
        onClick={onNext}
        className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-500"
      >
        Next
        <ArrowRightIcon className="h-4 w-4" />
      </button>
    </div>
  </div>
)

export default SoldPosStep
